import { useRouter } from "next/router";
import React, { ReactElement } from "react";

import axios from "axios";
import {
  FormikHelpers,
  FormikProps,
  FormikProvider,
  useFormik,
  useFormikContext,
} from "formik";
import debounce from "lodash/debounce";
import * as yup from "yup";

declare global {
  interface Window {
    dataLayer: any[] | undefined;
    blueshift: any;
    blueshiftProductId: string | undefined;
    location: Location;
  }
}

const phoneFormat = /^\([\d]{3}\) [\d]{3}-[\d]{4}$/;
const zipCodeFormat = /^[0-9]{5}(?:-[0-9]{4})?$/;

const checkEmailRequest = (email: string) => {
  return axios
    .post(
      "/api/lead-form-verify",
      {
        email,
      },
      {
        headers: {
          Authorization: `Bearer ${process.env.BS_API_AUTH_TOKEN}`,
        },
      }
    )
    .then(async (res) => {
      // console.log("res", res);

      return !res.data.found;
    })
    .catch((error) => {
      console.error(error);
      alert(error);
      return error.message;
    });
};

const checkEmail = debounce(checkEmailRequest, 400);
const validationObject: any = {
  firstName: yup.string().min(1).required("Please enter your first name."),
  lastName: yup.string().min(1).required("Please enter your last name."),
  phone: yup
    .string()
    .required("Please enter a valid phone number.")
    .test(
      "is-phone",
      "Phone format: (###) ###-####",
      (value) => !value || phoneFormat.test(value)
    ),
  zip: yup
    .string()
    .required("Please enter a valid zip code.")
    .test(
      "is-zipcode",
      "Please enter a valid zip code.",
      (value) => !value || zipCodeFormat.test(value)
    ),
  verification: yup.boolean().oneOf([true], "This field is required."),
};

const validationEmail = yup
  .string()

  .email("Please enter a valid email address.")
  .required("Please enter a valid email address.")
  .test(
    "email-registered",
    "This email is already registered.",
    async (value: any) => await checkEmailRequest(value)
  );

const validationSchema = yup.object().shape(validationObject);

export interface LeadFormDataTypes {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  zip: string;
  referrer_id: string;
  verification: boolean;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
}

export type ILeadFormWrapperProps = {
  formik: FormikProps<LeadFormDataTypes>;
  submitted: boolean;
};

const AutoSubmitToken = () => {
  // Grab values and submitForm from context
  const data = useFormikContext();
  const [sentFormStart, setSentFormStart] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Submit the form imperatively as an effect as soon as form values.token are 6 digits long

    if (data.dirty && !sentFormStart) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "formStart",
        });
        setSentFormStart(true);

        // console.log("Sent form start");
      }
    }
  }, [data.dirty]);
  return <span />;
};

function LeadForm({ children, data }: any): ReactElement {
  const formRef = React.useRef<HTMLFormElement>(null);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const router = useRouter();
  const { utm_campaign, utm_content, utm_medium, utm_source } = router.query;

  const { referrerId, refereeEmail, firstName, lastName } = data;

  if (referrerId) {
    validationObject.email = validationEmail;
  }

  const initialValues = {
    email: "",
    firstName: firstName || "",
    lastName: lastName || "",
    phone: "",
    zip: "",
    referrer_id: referrerId, //  need to get this from the URL, if referrerId comes in, hide email in form and show product interest
    refereeEmail, // comes in from the request on the email sent to the refereee from BlueShift
    verification: false,
    utm_campaign: "",
    utm_content: "",
    utm_medium: "",
    utm_source: "",
    product_family: "",
  };

  const formChanged = React.useRef(false);

  const handleSubmit = async (
    formData: LeadFormDataTypes,
    { setStatus, validateForm }: FormikHelpers<LeadFormDataTypes>
  ) => {
    const payload = {
      ...formData,

      xxTrustedFormCertUrl: formRef.current
        ? new FormData(formRef.current).get("xxTrustedFormCertUrl")
        : "",
    };

    if (initialValues.referrer_id && initialValues.refereeEmail) {
      // add referrer email information
      payload.referrer_id = initialValues.referrer_id;
      payload.email = initialValues.refereeEmail;
    }

    let utmFields = localStorage.getItem("query") as any;
    if (utmFields) {
      utmFields = JSON.parse(utmFields);
    }

    payload.utm_campaign =
      utm_campaign?.toString() || utmFields?.utm_campaign || "";
    payload.utm_content =
      utm_content?.toString() || utmFields?.utm_content || "";
    payload.utm_medium = utm_medium?.toString() || utmFields?.utm_medium || "";
    payload.utm_source = utm_source?.toString() || utmFields?.utm_source || "";

    const validationResponse = await validateForm(formData);

    if (Object.keys(validationResponse).length > 0) {
      setStatus(validationResponse);
      return false;
    }

    return axios
      .post("/api/lead-form-submit", payload)
      .then(() => {
        setSubmitted(true);
        if (utmFields) {
          localStorage.removeItem("query");
        }
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "formSubmit",
          });
        }

        if (window.blueshift && !referrerId) {
          window.blueshift.identify({
            email: formData.email,
          });

          // TODO: add LogRocket.identify("USER_ID_HERE", {
          //   name: "John Doe",
          //   email: formData.email,
          // });
        }
      })
      .catch((error) => {
        //TODO: need to log this error to something
        console.error(error);
        setStatus(error.message);
        return false;
      })
      .finally(() => {
        router.push("#form-top");
      });
  };

  const formik = useFormik<LeadFormDataTypes>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const t: ILeadFormWrapperProps = {
    formik,
    submitted,
  };

  return (
    <FormikProvider value={formik}>
      <div id="form-top" />
      <AutoSubmitToken />
      <form
        noValidate
        role="form"
        aria-label="Contact information"
        onSubmit={formik?.handleSubmit}
        ref={formRef}
      >
        {children(t)}
      </form>
    </FormikProvider>
  );
}

export default React.memo(LeadForm);
